import { Group, Loader, Text } from '@mantine/core';
import { formatElapsedSeconds } from './elapsed-format';
import { useElapsedSeconds } from './useElapsedSeconds';

type ThinkingIndicatorProps = {
  /**
   * ISO start of the turn being waited on (`ClientEventGroup.tStart`). See `useElapsedSeconds`
   * for why the count is taken from the turn rather than from this component's mount.
   */
  startedAt?: string;
};

/** @desc Past this, a wait is no longer "a moment" and saying "Thinking" starts to sound stuck. */
const STILL_WORKING_AFTER_SECONDS = 10;

/**
 * @desc The developer-mode placeholder shown while a turn is in flight, with the elapsed seconds
 * ticking beside it. The number is the point: an agent turn can legitimately run for a minute,
 * and a static "Thinking…" gives a waiting user no way to tell a slow answer from a dead one.
 * The label softens to "Still working…" once the wait is long enough that reassurance beats
 * repetition.
 *
 * Outside developer mode `ActivityIndicator` supersedes this, saying what the agent is actually
 * doing instead of that it is doing something.
 *
 * Mantine's Loader already respects reduced motion, so no extra guard is needed here.
 */
export function ThinkingIndicator({ startedAt }: ThinkingIndicatorProps) {
  const elapsed = useElapsedSeconds(startedAt);
  const label = elapsed >= STILL_WORKING_AFTER_SECONDS ? 'Still working…' : 'Thinking…';

  return (
    // A live region, as ActivityIndicator is: this is the whole of what a screen-reader user
    // gets while a turn runs, and without it the wait is silent. `aria-atomic`, so the label is
    // read as one phrase rather than word by word as it changes.
    <Group gap="xs" justify="center" py="sm" role="status" aria-live="polite" aria-atomic="true">
      <Loader size="sm" />
      <Text c="dimmed" fs="italic" size="sm">
        {label}
      </Text>
      {/* Hidden from the live region: the wait is worth announcing once, a per-second tick is
          not — inside it, a polite region would re-read the whole phrase every second. */}
      <Text aria-hidden="true" c="dimmed" fs="italic" size="sm">
        {formatElapsedSeconds(elapsed)}
      </Text>
    </Group>
  );
}
